import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// R1-UI-004 — single source of truth for the inactivity threshold.
// Override per-deploy via env later; for now 5 minutes is a sensible
// scribe-attention window.
export const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000;

// R1-#144 — stash key for the route the scribe was on when standby
// kicked in, so the standby screen's Resume button can take them back.
export const RESUME_ROUTE_KEY = "scribe-fe.resumeRoute";

// Paths that are *themselves* the standby destination. The hook is a
// no-op on these so the timer doesn't fire while the user is already
// where it would route them.
const STANDBY_PATHS = ["/setup", "/login"];

const ACTIVITY_EVENTS = [
  "pointerdown",
  "pointermove",
  "keydown",
  "touchstart",
  "wheel",
];

/**
 * Whenever the user is on a non-standby route, watch for activity. If
 * the user goes idle for `timeoutMs` milliseconds, navigate to
 * `redirectTo`. The watcher is suspended on standby paths so we don't
 * thrash when nothing is happening to navigate to.
 */
export function useInactivityRedirect({
  timeoutMs = INACTIVITY_TIMEOUT_MS,
  redirectTo = "/setup",
} = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const timerRef = useRef(null);
  const isStandby = STANDBY_PATHS.some((p) => location.pathname.startsWith(p));
  const currentPath = location.pathname;

  useEffect(() => {
    if (isStandby) return undefined;

    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        // R1-#144 — stash where the scribe was so standby's Resume
        // button can return them.
        try {
          sessionStorage.setItem(RESUME_ROUTE_KEY, currentPath);
        } catch {
          // sessionStorage unavailable (private browsing, SSR) —
          // non-fatal; resume falls back to the assigned class.
        }
        navigate(redirectTo, { replace: true });
      }, timeoutMs);
    };

    reset();
    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, reset, { passive: true });
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, reset);
      }
    };
  }, [isStandby, timeoutMs, redirectTo, navigate, currentPath]);
}
